import type { ComponentProps, ReactNode } from "react";
import {
  Alert,
  Button,
  Card,
  PageHeader,
  PageLayout,
  Stack,
  Text,
} from "@softmax/design-system";
import { LeagueIdentity } from "@softmax/web-ux/league-identity";
import { useLeagueSettings } from "./use-league-settings";

export function LeagueSettings({ leagueId }: { readonly leagueId: string }) {
  const editor = useLeagueSettings(leagueId);
  const { seed, draft, rename, uncertain, denied, name, refresh } = editor;
  let leagueIdentity: ReactNode = null;

  if (seed.data && !denied) {
    const record = seed.data;
    let nameField: ComponentProps<typeof LeagueIdentity>["name"] = {
      value: name,
      changed: draft !== null && name.trim() !== record.league_name,
      valid: name.trim().length > 0 && name.trim().length <= 200,
      pending: rename.isPending,
      unavailable: Boolean(seed.error) || uncertain,
      saved: rename.isSuccess,
      onChange: editor.changeName,
      onCancel: editor.cancelName,
      onSave: () => editor.saveName(record.id),
    };

    if (uncertain) {
      nameField = {
        ...nameField,
        error:
          "Could not confirm the save. Refresh league settings before trying again.",
      };
    } else if (rename.error) {
      nameField = { ...nameField, error: rename.error.message };
    }

    leagueIdentity = (
      <Card>
        <LeagueIdentity name={nameField} />
        {uncertain && !seed.error && (
          <Button disabled={seed.isFetching} onClick={() => void refresh()}>
            {seed.isFetching ? "Refreshing…" : "Refresh league settings"}
          </Button>
        )}
      </Card>
    );
  }

  return (
    <PageLayout>
      <Stack width="auto">
        <PageHeader
          title="League settings"
          description="Rename a league you own."
        />
        {seed.isPending && <Text>Loading league settings…</Text>}
        {seed.error && (
          <LeagueSettingsError
            error={seed.error}
            denied={denied}
            retained={Boolean(seed.data)}
            refreshing={seed.isFetching}
            onRefresh={() => void refresh()}
          />
        )}
        {leagueIdentity}
      </Stack>
    </PageLayout>
  );
}

function LeagueSettingsError({
  error,
  denied,
  retained,
  refreshing,
  onRefresh,
}: {
  readonly error: Error;
  readonly denied: boolean;
  readonly retained: boolean;
  readonly refreshing: boolean;
  readonly onRefresh: () => void;
}) {
  let message = error.message;

  if (denied)
    message =
      "League settings are unavailable. Sign in with an owner account for a league created through Softmax.";
  else if (retained)
    message = `League settings refresh failed: ${error.message}`;

  return (
    <Alert tone="danger">
      {message}
      {!denied && (
        <Button disabled={refreshing} onClick={onRefresh}>
          {refreshing ? "Refreshing…" : "Refresh league settings"}
        </Button>
      )}
    </Alert>
  );
}
